import { z } from 'zod';
import { screenIdSchema } from '../screens';
import { nonEmptyJsonPointerSchema } from './json-pointer';
import { replayMetadataSchema } from '../replay-privacy';

const navigationConditionSchema = z.union([
  z.string(),
  // Operator object, evaluated conjunctively (every present operator must
  // hold). `$in` tests membership/intersection; `$count` / `$minCount` test
  // the cardinality of the state value (scalars count as one) so a rule can
  // distinguish a single selection from a multi-selection at one state path.
  z
    .object({
      $in: z.array(z.string()).optional(),
      $count: z.number().int().nonnegative().optional(),
      $minCount: z.number().int().nonnegative().optional(),
    })
    .strict()
    .refine((c) => c.$in !== undefined || c.$count !== undefined || c.$minCount !== undefined, {
      message: 'condition object must carry at least one operator',
    }),
]);

/** A single navigation rule: from a flow-local step, optionally when a condition is met, go to a target */
export const navigationRuleSchema = z
  .object({
    from_step_key: z.string(),
    target_step_key: z.string(),
    when: z.record(nonEmptyJsonPointerSchema, navigationConditionSchema).optional(),
  })
  .strict();

export type NavigationRule = z.infer<typeof navigationRuleSchema>;

/**
 * A screen-level validation rule enforced by the host before advance.
 * `when` (same shape as a navigation rule's own `when`) makes the rule
 * conditional — required only while every keyed path's condition holds —
 * rather than applying unconditionally on every visit to the screen.
 */
export const validationRuleSchema = z
  .object({
    path: nonEmptyJsonPointerSchema,
    type: z.enum(['required', 'accepted']),
    message: z.string(),
    when: z.record(nonEmptyJsonPointerSchema, navigationConditionSchema).optional(),
  })
  .strict();

export type ValidationRule = z.infer<typeof validationRuleSchema>;

/** A screen within a flow */
export const flowScreenSchema = z
  .object({
    screen_id: screenIdSchema,
    spec: z
      .object({
        root: z.string(),
        // Element records are deliberately opaque at the envelope layer — their
        // shape (type, props, children, and the optional per-element `fallback`
        // naming a sibling element id that older clients substitute for an
        // unknown kind) is validated by the design-system catalog layer, which
        // owns the element vocabulary.
        elements: z.record(z.string(), z.unknown()),
      })
      .strict(),
    initial_state: z
      .object({
        screen: z.record(z.string(), z.unknown()).default({}),
      })
      .strict()
      .optional(),
    // Explicitly completes a branching screen when these live-answer conditions hold.
    completion_when: z
      .record(nonEmptyJsonPointerSchema, navigationConditionSchema)
      .refine((conditions) => Object.keys(conditions).length > 0, {
        message: 'completion_when must not be empty',
      })
      .optional(),
    validation: z.array(validationRuleSchema).optional(),
    replay: replayMetadataSchema.optional(),
  })
  .strict()
  .superRefine((screen, ctx) => {
    for (const elementKey of Object.keys(screen.replay?.elements ?? {})) {
      if (!(elementKey in screen.spec.elements)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['replay', 'elements', elementKey],
          message: 'replay metadata must reference a defined element',
        });
      }
    }
  });

export type FlowScreen = z.infer<typeof flowScreenSchema>;

/** Complete flow definition */
export const flowDefinitionSchema = z
  .object({
    schema_version: z.string(),
    flow_id: z.string(),
    flow_version: z.string(),
    locale: z.string().optional(),
    label: z.string().optional(),
    description: z.string().optional(),
    initial_step_key: z.string(),
    progress_step_keys: z.array(z.string()).nonempty().optional(),
    // Ordered, non-empty groups of progress step keys. When present, the
    // progress indicator is scoped per section (each group renders its own
    // dot counter) and sections supersede `progress_step_keys` for progress
    // derivation; `progress_step_keys` remains valid for section-less flows.
    progress_sections: z.array(z.array(z.string()).nonempty()).nonempty().optional(),
    // How the progress total treats a declared progress step the resolved path
    // does not visit. `declared` (the default) keeps it, so a conditional step
    // holds its place in the count whether or not it is shown; `resolved` drops
    // it, so a skipped step leaves the indicator instead of making it jump. A
    // flow whose skips are the norm (a questionnaire gate) opts into `resolved`.
    progress_scope: z.enum(['declared', 'resolved']).optional(),
    screens: z.record(z.string(), flowScreenSchema),
    navigation: z.array(navigationRuleSchema),
    analytics: z.record(z.string(), z.string()).optional(),
    fallback: z
      .object({ type: z.literal('host') })
      .strict()
      .optional(),
  })
  .strict()
  .refine((data) => data.initial_step_key in data.screens, {
    message: 'initial_step_key must reference a defined screen',
  })
  .refine(
    (data) =>
      data.navigation.every(
        (rule) => rule.from_step_key in data.screens && rule.target_step_key in data.screens,
      ),
    {
      message: 'All navigation rule step references must exist in screens',
    },
  )
  .refine(
    (data) =>
      !data.progress_step_keys ||
      data.progress_step_keys.every((stepKey) => stepKey in data.screens),
    {
      message: 'progress_step_keys must reference defined screens',
    },
  )
  .refine(
    (data) =>
      !data.progress_sections ||
      data.progress_sections.every((section) =>
        section.every((stepKey) => stepKey in data.screens),
      ),
    {
      message: 'progress_sections must reference defined screens',
    },
  );

export type FlowDefinition = z.infer<typeof flowDefinitionSchema>;

/** Adapter interface for flow state management. Callers supply an implementation. */
export interface FlowStateAdapter {
  /** Accumulated answers keyed by JSON Pointer, or nested state addressed by JSON Pointer */
  answers: Record<string, unknown>;
  /** Update a single JSON Pointer-addressed answer */
  updateAnswer: (key: string, value: unknown) => void;
  /**
   * Merge multiple pointer-addressed values synchronously. Immediate flow tools may rely on the
   * write being visible to subsequent tools in the same dispatch chain.
   */
  mergeAnswers: (answers: Record<string, unknown>) => void;
  /** Reset all state */
  reset: () => void;
}
